import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { AU_STATES, shortDate } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, MapPinned } from "lucide-react";

export default function Trips() {
  const [trips, setTrips] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [agents, setAgents] = useState([]);
  const [customers, setCustomers] = useState([]);
  useEffect(() => {
    api.get("/trips").then(r => setTrips(r.data));
    api.get("/agents").then(r => setAgents(r.data));
    api.get("/customers").then(r => setCustomers(r.data));
  }, []);
  const reload = () => api.get("/trips").then(r => setTrips(r.data));
  return (
    <div className="space-y-6" data-testid="trips-page">
      <div className="flex justify-between items-end gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl sm:text-4xl font-heading font-black tracking-tight">Trips</h1>
          <p className="text-slate-600 mt-1">Regional visit planning & outcome tracking</p>
        </div>
        <button onClick={() => setShowNew(true)} className="mlily-btn h-11 px-5 rounded-lg font-semibold flex items-center gap-2" data-testid="new-trip-btn"><Plus className="w-4 h-4" /> New Trip</button>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {trips.map(t => (
          <Link key={t.id} to={`/trips/${t.id}`} className="bg-white rounded-xl border border-slate-200 p-5 card-hoverable block" data-testid={`trip-card-${t.id}`}>
            <div className="flex justify-between items-start">
              <MapPinned className="w-5 h-5 text-[#003DA5]" />
              <div className="text-xs text-slate-500">{shortDate(t.start_date)} – {shortDate(t.end_date)}</div>
            </div>
            <div className="mt-3 text-lg font-heading font-semibold">{t.name}</div>
            <div className="text-sm text-slate-500">{t.town}, {t.state}</div>
            <div className="mt-3 text-xs text-slate-500">Agent: <span className="font-semibold text-slate-700">{t.agent_name}</span></div>
            <div className="mt-2 text-xs text-slate-500">Stores planned: {t.stores_planned?.length || 0}</div>
          </Link>
        ))}
        {trips.length === 0 && <div className="col-span-full text-center py-16 text-slate-500">No trips yet.</div>}
      </div>
      {showNew && <NewTrip agents={agents} customers={customers} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); reload(); }} />}
    </div>
  );
}

function NewTrip({ agents, customers, onClose, onSaved }) {
  const [f, setF] = useState({ name: "", agent_id: "", state: "NSW", town: "", start_date: "", end_date: "", stores_planned: [], travel_notes: "", expenses: 0 });
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/trips", { ...f, expenses: Number(f.expenses) || 0 });
      toast.success("Trip created"); onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4">
      <form onSubmit={submit} className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b flex justify-between sticky top-0 bg-white"><h3 className="text-xl font-heading font-semibold">New Trip</h3><button type="button" onClick={onClose}><X className="w-5 h-5" /></button></div>
        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <F label="Trip Name *"><input required value={f.name} onChange={upd("name")} className="w-full h-11 px-3 border rounded-lg" data-testid="trip-name" /></F>
          <F label="Agent *">
            <select required value={f.agent_id} onChange={upd("agent_id")} className="w-full h-11 px-3 border rounded-lg" data-testid="trip-agent">
              <option value="">—</option>{agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </F>
          <F label="State"><select value={f.state} onChange={upd("state")} className="w-full h-11 px-3 border rounded-lg">{AU_STATES.map(s => <option key={s}>{s}</option>)}</select></F>
          <F label="Town"><input value={f.town} onChange={upd("town")} className="w-full h-11 px-3 border rounded-lg" /></F>
          <F label="Start Date *"><input type="date" required value={f.start_date} onChange={upd("start_date")} className="w-full h-11 px-3 border rounded-lg" /></F>
          <F label="End Date *"><input type="date" required value={f.end_date} onChange={upd("end_date")} className="w-full h-11 px-3 border rounded-lg" /></F>
          <F label="Expenses (AUD)"><input type="number" value={f.expenses} onChange={upd("expenses")} className="w-full h-11 px-3 border rounded-lg" /></F>
          <div className="col-span-full">
            <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Stores Planned</label>
            <select multiple value={f.stores_planned} onChange={(e) => setF({ ...f, stores_planned: Array.from(e.target.selectedOptions).map(o => o.value) })} className="mt-1 w-full h-40 p-2 border rounded-lg">
              {customers.filter(c => !f.state || c.state === f.state).map(c => <option key={c.id} value={c.id}>{c.name} — {c.town}</option>)}
            </select>
            <div className="text-xs text-slate-500 mt-1">Hold Ctrl/Cmd to select multiple.</div>
          </div>
          <div className="col-span-full"><label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Travel Notes</label><textarea value={f.travel_notes} onChange={upd("travel_notes")} rows={3} className="w-full mt-1 p-3 border rounded-lg" /></div>
        </div>
        <div className="p-5 border-t flex justify-end gap-2"><button type="button" onClick={onClose} className="h-11 px-5 border rounded-lg">Cancel</button><button type="submit" className="mlily-btn h-11 px-5 rounded-lg font-semibold" data-testid="save-trip-btn">Save Trip</button></div>
      </form>
    </div>
  );
}
const F = ({ label, children }) => <div><label className="text-xs uppercase tracking-wider font-semibold text-slate-500">{label}</label><div className="mt-1">{children}</div></div>;
